import type { FunctionComponent } from 'react';
import { FormattedDate } from 'react-intl';

import { FormattedMessage } from '../../../shared/components/formatted-message/formatted-message';
import { Icon } from '../../../shared/components/icon/icon';
import { createStyleHelper } from '../../../shared/utils/class-names';
import { ensureIsoDateWithTimeZone } from '../../../shared/utils/date';
import { slugify } from '../../../shared/utils/strings';
import type { IActivity } from '../../reducers/activity-feed.reducer';
import { MoreInfo } from '../more-info/more-info';

import styles from './activity-table.module.scss';

const style = createStyleHelper(styles, 'activity-table');

export interface IActivityTableProps {
    onLoadMore: () => void;
    hasMore: boolean;
    activities: IActivity[];
}

export const ActivityTable: FunctionComponent<IActivityTableProps> = ({
    activities,
    onLoadMore,
    hasMore,
}) => {
    return (
        <>
            <table className={style()}>
                <thead>
                    <tr className={style('headings')}>
                        <th className={style('heading', ['activity'])}>
                            <FormattedMessage
                                id="activity-table.heading.activity"
                                defaultMessage="Activity"
                            />
                        </th>
                        <th className={style('heading', ['time'])}>
                            <FormattedMessage
                                id="activity-table.heading.date-time"
                                defaultMessage="Date &amp; Time"
                            />
                        </th>
                        <th className={style('heading', ['status'])}>
                            <FormattedMessage
                                id="activity-table.heading.status"
                                defaultMessage="Status"
                            />
                        </th>

                        <th className={style('heading', ['info'])}>
                            <FormattedMessage
                                id="activity-table.heading.more-information"
                                defaultMessage="More Info"
                            />
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {activities.map(({ id, status, time, activity, metadata }) => (
                        <tr key={id} className={style('row')}>
                            <td className={style('item', ['activity'])}>
                                <FormattedMessage
                                    id={`activity-table.activity.${slugify(activity)}`}
                                    defaultMessage={activity}
                                    values={metadata as Record<string, string>}
                                />
                                <span className={style('inline-time')}>
                                    <FormattedDate
                                        value={ensureIsoDateWithTimeZone(time)}
                                        dateStyle={'short'}
                                        timeStyle={'short'}
                                    />
                                </span>
                            </td>
                            <td className={style('item', ['time'])}>
                                <FormattedDate
                                    value={ensureIsoDateWithTimeZone(time)}
                                    dateStyle={'short'}
                                    timeStyle={'short'}
                                />
                            </td>
                            <td className={style('item', ['status'])}>
                                <FormattedMessage
                                    id={`activity-table.activity.${status}`}
                                    defaultMessage={status}
                                />
                            </td>
                            <td className={style('item', ['info'])}>
                                <MoreInfo activity={activity} activityMetadata={metadata} />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {hasMore && activities.length ? (
                <button onClick={onLoadMore} className={style('load-more')}>
                    <Icon name={'caret-down'} />
                </button>
            ) : null}
        </>
    );
};
